import {
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
import {
	AppMode,
	BroadLineOfBusiness,
	EligibilityStatusCode,
	NewBusinessApplicationStatus,
	NotificationType,
} from '@app/enumerations';
// Import api models.
import {
	AcknowledgementText,
    AddressLocationAsMailingAddress,
	BindingAuthorityEligibilityResult,
	BuildingRequestCommercialExisting,
	BuildingRequestPersonal,
	DisqualifyingConditionsAgreement,
	DocumentUpload,
	ErrorMessage,
	InsuredInfo,
	MortgageeRequest,
	NewBusinessApplication,
	NewBusinessApplicationDocument,
	PerilDeductible,
	PossibleDisclosureText,
	PossibleDisclosureTexts,
	PossiblePeril,
	Signature,
} from '@app/models/api';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addAppMode,
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// Import actions.
import {
	PolicyAction,
} from '@app/redux/actions';
// Import action parameters.
import {
	OverlayTargetParameters,
	PolicyParameters,
} from './ActionParametersBarrel';

interface NewBusinessApplicationAction extends PolicyAction {

}

interface NewBusinessApplicationParameters extends PolicyParameters {

}

interface NewBusinessBuildingParameters extends NewBusinessApplicationParameters {
	buildingGuid: string;
}

interface NewBusinessMortgageeParameters extends NewBusinessApplicationParameters {
	mortgageeGuid: string;
}

interface RateNewBusinessApplicationParameters extends NewBusinessApplicationParameters {
	isRateCalculation?: boolean;
	perilsDeductibles?: PerilDeductible[];
	disqualifyingConditionsAgreement?: DisqualifyingConditionsAgreement;
}


// Load New Business Application Action.

type LoadNewBusinessApplicationParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

interface LoadNewBusinessApplicationAction extends NewBusinessApplicationAction {
	newBusinessApplication: NewBusinessApplication;
	newBusinessApplicationDocument?: NewBusinessApplicationDocument;
}

const loadNewBusinessApplicationAction: (newBusinessApplication: NewBusinessApplication, newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined, params: NewBusinessApplicationParameters) =>
	LoadNewBusinessApplicationAction = (newBusinessApplication: NewBusinessApplication, newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined, params: NewBusinessApplicationParameters) => ({
		...params,
		newBusinessApplication: newBusinessApplication,
		newBusinessApplicationDocument: newBusinessApplicationDocument,
		type: ActionType.LOAD_NEW_BUSINESS_APPLICATION,
	});

const loadNewBusinessApplication = (params: LoadNewBusinessApplicationParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadNewBusinessApplication`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(params.policyId);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(params.policyId);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, params));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(params.policyId, newBusinessPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Start New Business Application Action.

interface StartNewBusinessApplicationParameters extends OverlayTargetParameters {
	broadLineOfBusiness: BroadLineOfBusiness;
	propertyAddress: AddressLocationAsMailingAddress;
	perilsDeductibles: PerilDeductible[],
	disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement,
}

const startNewBusinessApplication = (params: StartNewBusinessApplicationParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-startNewBusinessApplication`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = params.broadLineOfBusiness === BroadLineOfBusiness.PERSONAL
				? await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().startNewBusinessApplicationPersonal(params.propertyAddress, params.perilsDeductibles, params.disqualifyingConditionsAgreement)
				: await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().startNewBusinessApplicationCommercial(params.propertyAddress, params.perilsDeductibles, params.disqualifyingConditionsAgreement);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Saving the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// Check to see if the new business application is currently declined.
			let isDeclined: boolean = newBusinessApplication.status?.status?.statusCode === NewBusinessApplicationStatus.DECLINED; 
			// If the new business application result has failed eligibility, then flag the eligibility notification popup to appear.
			let isFailedEligibility: boolean = newBusinessApplication.status?.eligibility?.statusCode === EligibilityStatusCode.FAIL;
			let problems: string[] = newBusinessApplication.status?.eligibility?.problems ?? [];
			if (!isDeclined
				&& isFailedEligibility
				&& problems.length > 0) {
				await addAppMode({
					appMode: AppMode.ELIGIBILITY_NOTIFICATION,
				})(dispatch);
			}

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Rate New Business Application Personal Action.

interface RateNewBusinessApplicationPersonalParameters extends RateNewBusinessApplicationParameters, OverlayTargetParameters {
	risk?: BuildingRequestPersonal;
}

const rateNewBusinessApplicationPersonal = (params: RateNewBusinessApplicationPersonalParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-rateNewBusinessApplicationPersonal`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = params.isRateCalculation
				? ['Updating application...']
				: ['Updating application...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					// Start processing lloyds of london.
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().rateNewBusinessApplicationPersonal(params.policyId, params.perilsDeductibles, params.risk, params.disqualifyingConditionsAgreement);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Updating the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// Check to see if the new business application is currently declined.
			let isDeclined: boolean = newBusinessApplication.status?.status?.statusCode === NewBusinessApplicationStatus.DECLINED; 
			// If the new business application result has failed eligibility, then flag the eligibility notification popup to appear.
			let isFailedEligibility: boolean = newBusinessApplication.status?.eligibility?.statusCode === EligibilityStatusCode.FAIL;
			let problems: string[] = newBusinessApplication.status?.eligibility?.problems ?? [];
			if (!isDeclined
				&& isFailedEligibility
				&& problems.length > 0) {
				await addAppMode({
					appMode: AppMode.ELIGIBILITY_NOTIFICATION,
				})(dispatch);
			}

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Rate New Business Application Commercial Action.

interface RateNewBusinessApplicationCommercialParameters extends RateNewBusinessApplicationParameters, OverlayTargetParameters {
	includeTriaCoverage?: boolean;
	risks?: BuildingRequestCommercialExisting[];
}

const rateNewBusinessApplicationCommercial = (params: RateNewBusinessApplicationCommercialParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-rateNewBusinessApplicationCommercial`;
		try {
			// Set up the process messages to be displayed.
			let messages: string[] = params.isRateCalculation
				? ['Updating application...']
				: ['Updating application...'];

			// Start processing.
			let timeoutInstances: NodeJS.Timeout[] = messages.map((message: string, index: number) => {
				return setTimeout(() => {
					// Start processing lloyds of london.
					startProcessing({
						message: message,
						messageId: messageId,
						overlayTarget: params.overlayTarget,
					})(dispatch);
				}, index * 3000)
			});

			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().rateNewBusinessApplicationCommercial(params.policyId, params.includeTriaCoverage, params.perilsDeductibles, params.risks, params.disqualifyingConditionsAgreement);

			// Clear out all timeout instances.
			timeoutInstances.forEach((timeoutInstance: NodeJS.Timeout) => { clearTimeout(timeoutInstance) });

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Updating the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// Check to see if the new business application is currently declined.
			let isDeclined: boolean = newBusinessApplication.status?.status?.statusCode === NewBusinessApplicationStatus.DECLINED; 
			// If the new business application result has failed eligibility, then flag the eligibility notification popup to appear.
			let isFailedEligibility: boolean = newBusinessApplication.status?.eligibility?.statusCode === EligibilityStatusCode.FAIL;
			let problems: string[] = newBusinessApplication.status?.eligibility?.problems ?? [];
			if (!isDeclined
				&& isFailedEligibility
				&& problems.length > 0) {
				await addAppMode({
					appMode: AppMode.ELIGIBILITY_NOTIFICATION,
				})(dispatch);
			}

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Delete New Business Building Action.

type DeleteNewBusinessBuildingParameters = NewBusinessBuildingParameters & OverlayTargetParameters;

const deleteNewBusinessBuilding = (params: DeleteNewBusinessBuildingParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-deleteNewBusinessBuilding`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Removing building...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to remove building.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().deleteBuilding(params.buildingGuid);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Removing the building did not return a valid application id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// Check to see if the new business application is currently declined.
			let isDeclined: boolean = newBusinessApplication.status?.status?.statusCode === NewBusinessApplicationStatus.DECLINED; 
			// If the new business application result has failed eligibility, then flag the eligibility notification popup to appear.
			let isFailedEligibility: boolean = newBusinessApplication.status?.eligibility?.statusCode === EligibilityStatusCode.FAIL;
			let problems: string[] = newBusinessApplication.status?.eligibility?.problems ?? [];
			if (!isDeclined
				&& isFailedEligibility
				&& problems.length > 0) {
				await addAppMode({
					appMode: AppMode.ELIGIBILITY_NOTIFICATION,
				})(dispatch);
			}

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Select New Business Building Action.

type SelectNewBusinessBuildingParameters = NewBusinessBuildingParameters;

interface SelectNewBusinessBuildingAction extends NewBusinessApplicationAction {
	buildingGuid: string;
}

const selectNewBusinessBuildingAction: (params: SelectNewBusinessBuildingParameters) =>
	SelectNewBusinessBuildingAction = (params: SelectNewBusinessBuildingParameters) => ({
		...params,
		type: ActionType.SELECT_NEW_BUSINESS_BUILDING,
	});

const selectNewBusinessBuilding = (params: SelectNewBusinessBuildingParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Select building on redux.
			dispatch<SelectNewBusinessBuildingAction>(selectNewBusinessBuildingAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Unselect New Business Building Action.

type UnselectNewBusinessBuildingParameters = NewBusinessApplicationParameters;

type UnselectNewBusinessBuildingAction = NewBusinessApplicationAction;

const unselectNewBusinessBuildingAction: (params: UnselectNewBusinessBuildingParameters) =>
	UnselectNewBusinessBuildingAction = (params: UnselectNewBusinessBuildingParameters) => ({
		...params,
		type: ActionType.UNSELECT_NEW_BUSINESS_BUILDING,
	});

const unselectNewBusinessBuilding = (params: UnselectNewBusinessBuildingParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Unselect building on redux.
			dispatch<UnselectNewBusinessBuildingAction>(unselectNewBusinessBuildingAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Save New Business Insured Info Action.

interface SaveNewBusinessInsuredInfoParameters extends NewBusinessApplicationParameters, OverlayTargetParameters {
	insuredInfo: InsuredInfo;
}

const saveNewBusinessInsuredInfo = (params: SaveNewBusinessInsuredInfoParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-saveNewBusinessInsuredInfo`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving insured information...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().saveInsuredInfo(params.policyId, params.insuredInfo);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Submitting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Add New Business Mortgagee Action.

interface AddNewBusinessMortgageeParameters extends NewBusinessApplicationParameters, OverlayTargetParameters {
	mortgagee: MortgageeRequest;
}

const addNewBusinessMortgagee = (params: AddNewBusinessMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-addNewBusinessMortgagee`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving mortgagee information...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save the mortgagee.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().addMortgagee(params.policyId, params.mortgagee);
			// After saving the mortgagee, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Getting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Update New Business Mortgagee Action.

interface UpdateNewBusinessMortgageeParameters extends NewBusinessMortgageeParameters, OverlayTargetParameters {
	mortgagee: MortgageeRequest
}

const updateNewBusinessMortgagee = (params: UpdateNewBusinessMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updateNewBusinessMortgagee`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving mortgagee information...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save the mortgagee.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().updateMortgagee(params.mortgageeGuid, params.mortgagee);
			// After saving the mortgagee, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Getting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Delete New Business Mortgagee Action.

type DeleteNewBusinessMortgageeParameters = NewBusinessMortgageeParameters & OverlayTargetParameters;

const deleteNewBusinessMortgagee = (params: DeleteNewBusinessMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-deleteNewBusinessMortgagee`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Deleting mortgagee information...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to delete the mortgagee.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().deleteMortgagee(params.mortgageeGuid);
			// After deleting the mortgagee, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Getting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Confirm No Mortgagees New Business Action.

type ConfirmNoMortgageesNewBusinessParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

const confirmNoMortgageesNewBusiness = (params: ConfirmNoMortgageesNewBusinessParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-confirmNoMortgageesNewBusiness`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to confirm no mortgagees.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().confirmNoMortgagees(params.policyId);
			// After confirming no mortgagees, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Getting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Validate Mortgagees New Business Action.

type ValidateMortgageesNewBusinessParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

const validateMortgageesNewBusiness = (params: ValidateMortgageesNewBusinessParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-validateMortgageesNewBusiness`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to validate the mortgagees.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().validateMortgagees(params.policyId);
			// After validating the mortgagees, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Getting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Select New Business Mortgagee Action.

type SelectNewBusinessMortgageeParameters = NewBusinessMortgageeParameters;

interface SelectNewBusinessMortgageeAction extends NewBusinessApplicationAction {
	mortgageeGuid: string;
}

const selectNewBusinessMortgageeAction: (params: SelectNewBusinessMortgageeParameters) =>
	SelectNewBusinessMortgageeAction = (params: SelectNewBusinessMortgageeParameters) => ({
		...params,
		type: ActionType.SELECT_NEW_BUSINESS_MORTGAGEE,
	});

const selectNewBusinessMortgagee = (params: SelectNewBusinessMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Select mortgagee on redux.
			dispatch<SelectNewBusinessMortgageeAction>(selectNewBusinessMortgageeAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};


// Unselect New Business Mortgagee Action.

type UnselectNewBusinessMortgageeParameters = NewBusinessApplicationParameters;

type UnselectNewBusinessMortgageeAction = NewBusinessApplicationAction;

const unselectNewBusinessMortgageeAction: (params: UnselectNewBusinessMortgageeParameters) =>
	UnselectNewBusinessMortgageeAction = (params: UnselectNewBusinessMortgageeParameters) => ({
		...params,
		type: ActionType.UNSELECT_NEW_BUSINESS_MORTGAGEE,
	});

const unselectNewBusinessMortgagee = (params: UnselectNewBusinessMortgageeParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Unselect mortgagee on redux.
			dispatch<UnselectNewBusinessMortgageeAction>(unselectNewBusinessMortgageeAction(params));
		}
		catch (error: any) {
			// Throw error;
			throw error;
		}
	};

// Save New Business Inception Date Action.

interface SaveNewBusinessInceptionDateParameters extends NewBusinessApplicationParameters, OverlayTargetParameters {
	inceptionDate: Date;
}

const saveNewBusinessInceptionDate = (params: SaveNewBusinessInceptionDateParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-saveNewBusinessInceptionDate`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Saving inception date...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().saveInceptionDate(params.policyId, params.inceptionDate);

			// After saving the inception date, fetch the new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Submitting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};



// Submit New Business Application Action.

interface SubmitNewBusinessApplicationParameters extends NewBusinessApplicationParameters, OverlayTargetParameters {
	signature: Signature;
	acknowledgements: AcknowledgementText[];
}

const submitNewBusinessApplication = (params: SubmitNewBusinessApplicationParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-submitNewBusinessApplication`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Submitting application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().submitNewBusinessApplication(params.policyId, params.acknowledgements, params.signature);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Submitting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Unsubmit New Business Application Action.

type UnsubmitNewBusinessApplicationParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

const unsubmitNewBusinessApplication = (params: UnsubmitNewBusinessApplicationParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-unsubmitNewBusinessApplication`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Unsubmitting application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().unsubmitNewBusinessApplication(params.policyId);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Unsubmitting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}
			// Call api to load the possible perils and deductibles.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(newBusinessApplication.id);

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// Load the new-business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(newBusinessApplication.id, newBusinessPossiblePerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Resubmit New Business Application Action.

interface ResubmitNewBusinessApplicationParameters extends NewBusinessApplicationParameters, OverlayTargetParameters {
	note: string;
	document?: DocumentUpload;
}

const resubmitNewBusinessApplication = (params: ResubmitNewBusinessApplicationParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplication> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-resubmitNewBusinessApplication`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Resubmitting application to underwriting...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to save new business application.
			let newBusinessApplication: NewBusinessApplication = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().resubmitNewBusinessApplication(params.policyId, params.note, params.document);

			if (newBusinessApplication.id === null) {
				throw new ErrorMessage(400, 'Unable to fetch application id.', 'Resubmitting the application did not return a valid id.');
			}

			// Attempt to get the new business application document, if it's available.
			let isNewBusinessApplicationDocumentAvailable: boolean = newBusinessApplication.status?.isApplicationDocumentAvailable ?? false;
			let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = undefined;
			if (isNewBusinessApplicationDocumentAvailable) {
				newBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(newBusinessApplication.id);
			}

			// Load new business application to redux.
			dispatch<LoadNewBusinessApplicationAction>(loadNewBusinessApplicationAction(newBusinessApplication, newBusinessApplicationDocument, { policyId: newBusinessApplication.id }));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplication; 
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Load Possible New Business Disclosure Texts Action.

type LoadPossibleNewBusinessDisclosureTextsParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

interface LoadPossibleNewBusinessDisclosureTextsAction extends NewBusinessApplicationAction {
	possibleDisclosureTexts: PossibleDisclosureText[];
}

const loadPossibleNewBusinessDisclosureTextsAction: (possibleDisclosureTexts: PossibleDisclosureText[], params: NewBusinessApplicationParameters) =>
	LoadPossibleNewBusinessDisclosureTextsAction = (possibleDisclosureTexts: PossibleDisclosureText[], params: NewBusinessApplicationParameters) => ({
		...params,
		possibleDisclosureTexts: possibleDisclosureTexts,
		type: ActionType.LOAD_POSSIBLE_NEW_BUSINESS_DISCLOSURE_TEXTS,
	});

const loadPossibleNewBusinessDisclosureTexts = (params: LoadPossibleNewBusinessDisclosureTextsParameters) =>
	async (dispatch: Dispatch): Promise<PossibleDisclosureText[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadPossibleNewBusinessDisclosureTexts`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading disclosure texts...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load possible disclosure texts.
			let possibleDisclosureTexts: PossibleDisclosureTexts = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossibleDisclosureTexts(params.policyId);
			// Load possible disclosure texts to redux.
			dispatch<LoadPossibleNewBusinessDisclosureTextsAction>(loadPossibleNewBusinessDisclosureTextsAction(possibleDisclosureTexts.disclosureTexts, params));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return possibleDisclosureTexts.disclosureTexts;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Load New Business Application Document Action.

type LoadNewBusinessApplicationDocumentParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

interface LoadNewBusinessApplicationDocumentAction extends NewBusinessApplicationAction {
	newBusinessApplicationDocument: NewBusinessApplicationDocument;
}

const loadNewBusinessApplicationDocumentAction: (newBusinessApplicationDocument: NewBusinessApplicationDocument, params: NewBusinessApplicationParameters) =>
	LoadNewBusinessApplicationDocumentAction = (newBusinessApplicationDocument: NewBusinessApplicationDocument, params: NewBusinessApplicationParameters) => ({
		...params,
		newBusinessApplicationDocument: newBusinessApplicationDocument,
		type: ActionType.LOAD_NEW_BUSINESS_APPLICATION_DOCUMENT,
	});

const loadNewBusinessApplicationDocument = (params: LoadNewBusinessApplicationDocumentParameters) =>
	async (dispatch: Dispatch): Promise<NewBusinessApplicationDocument> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadNewBusinessApplicationDocument`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading application document...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load new business application document.
			let newBusinessApplicationDocument: NewBusinessApplicationDocument = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getNewBusinessApplicationDocument(params.policyId);
			// Load new business application document to redux.
			dispatch<LoadNewBusinessApplicationDocumentAction>(loadNewBusinessApplicationDocumentAction(newBusinessApplicationDocument, params));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessApplicationDocument;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Load New Business Binding Authority Eligibility Results Action.

type LoadNewBusinessBindingAuthorityEligibilityResultsParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

interface LoadNewBusinessBindingAuthorityEligibilityResultsAction extends NewBusinessApplicationAction {
	bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[];
}

const loadNewBusinessBindingAuthorityEligibilityResultsAction: (bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[], params: NewBusinessApplicationParameters) =>
	LoadNewBusinessBindingAuthorityEligibilityResultsAction = (bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[], params: NewBusinessApplicationParameters) => ({
		...params,
		bindingAuthorityEligibilityResults: bindingAuthorityEligibilityResults,
		type: ActionType.LOAD_NEW_BUSINESS_BINDING_AUTHORITY_ELIGIBILITY_RESULTS,
	});

const loadNewBusinessBindingAuthorityEligibilityResults = (params: LoadNewBusinessBindingAuthorityEligibilityResultsParameters) =>
	async (dispatch: Dispatch): Promise<BindingAuthorityEligibilityResult[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadNewBusinessBindingAuthorityEligibilityResults`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading eligibility results...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load eligibility results.
			let bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getBindingAuthorityEligibilityResults(params.policyId);
			// Load eligibility results to redux.
			dispatch<LoadNewBusinessBindingAuthorityEligibilityResultsAction>(loadNewBusinessBindingAuthorityEligibilityResultsAction(bindingAuthorityEligibilityResults, params));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return bindingAuthorityEligibilityResults;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Load New Business Possible Perils Action.

interface LoadNewBusinessPossiblePerilsAction extends NewBusinessApplicationAction {
	newBusinessPossiblePerils: PossiblePeril[],
}

type LoadNewBusinessPossiblePerilsParameters = NewBusinessApplicationParameters & OverlayTargetParameters;

const loadNewBusinessPossiblePerilsAction: (policyId: number, newBusinessPossiblePerils: PossiblePeril[]) =>
	LoadNewBusinessPossiblePerilsAction = (policyId: number, newBusinessPossiblePerils: PossiblePeril[]) => ({
		policyId,
		newBusinessPossiblePerils,
		type: ActionType.LOAD_NEW_BUSINESS_POSSIBLE_PERILS,
	});

const loadNewBusinessPossiblePerils = (params: LoadNewBusinessPossiblePerilsParameters) =>
	async (dispatch: Dispatch): Promise<PossiblePeril[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadNewBusinessPossiblePerils`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading possible perils for application...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Call api to load new business possible perils.
			let newBusinessPossiblePerils: PossiblePeril[] = await ControllerFactoryProvider.getControllerFactory().getNewBusinessApplicationController().getPossiblePerilsAndDeductibles(params.policyId);

			// Load new business possible perils to redux.
			dispatch<LoadNewBusinessPossiblePerilsAction>(loadNewBusinessPossiblePerilsAction(params.policyId, newBusinessPossiblePerils));
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return newBusinessPossiblePerils;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Export actions.
export {
	NewBusinessApplicationAction,
	LoadNewBusinessApplicationAction,
	SelectNewBusinessBuildingAction,
	UnselectNewBusinessBuildingAction,
	SelectNewBusinessMortgageeAction,
	UnselectNewBusinessMortgageeAction,
	LoadPossibleNewBusinessDisclosureTextsAction,
	LoadNewBusinessApplicationDocumentAction,
	LoadNewBusinessBindingAuthorityEligibilityResultsAction,
	LoadNewBusinessPossiblePerilsAction,
};

// Export action methods.
export {
	loadNewBusinessApplication,
	startNewBusinessApplication,
	rateNewBusinessApplicationPersonal,
	rateNewBusinessApplicationCommercial,
	deleteNewBusinessBuilding,
	selectNewBusinessBuilding,
	unselectNewBusinessBuilding,
	saveNewBusinessInsuredInfo,
	addNewBusinessMortgagee,
	updateNewBusinessMortgagee,
	deleteNewBusinessMortgagee,
	confirmNoMortgageesNewBusiness,
	validateMortgageesNewBusiness,
	selectNewBusinessMortgagee,
	unselectNewBusinessMortgagee,
	saveNewBusinessInceptionDate,
	submitNewBusinessApplication,
	resubmitNewBusinessApplication,
	unsubmitNewBusinessApplication,
	loadPossibleNewBusinessDisclosureTexts,
	loadNewBusinessApplicationDocument,
	loadNewBusinessBindingAuthorityEligibilityResults,
	loadNewBusinessPossiblePerils,
};

// Export action parameters.
export {
	NewBusinessApplicationParameters,
	NewBusinessBuildingParameters,
	LoadNewBusinessApplicationParameters,
	StartNewBusinessApplicationParameters,
	RateNewBusinessApplicationParameters,
	RateNewBusinessApplicationPersonalParameters,
	RateNewBusinessApplicationCommercialParameters,
	DeleteNewBusinessBuildingParameters,
	SelectNewBusinessBuildingParameters,
	UnselectNewBusinessBuildingParameters,
	SaveNewBusinessInsuredInfoParameters,
	AddNewBusinessMortgageeParameters,
	UpdateNewBusinessMortgageeParameters,
	DeleteNewBusinessMortgageeParameters,
	ConfirmNoMortgageesNewBusinessParameters,
	ValidateMortgageesNewBusinessParameters,
	SelectNewBusinessMortgageeParameters,
	UnselectNewBusinessMortgageeParameters,
	SaveNewBusinessInceptionDateParameters,
	SubmitNewBusinessApplicationParameters,
	UnsubmitNewBusinessApplicationParameters,
	ResubmitNewBusinessApplicationParameters,
	LoadPossibleNewBusinessDisclosureTextsParameters,
	LoadNewBusinessApplicationDocumentParameters,
	LoadNewBusinessBindingAuthorityEligibilityResultsParameters,
	LoadNewBusinessPossiblePerilsParameters,
};

// Export action factories.
export {
	loadNewBusinessApplicationAction,
	selectNewBusinessBuildingAction,
	unselectNewBusinessBuildingAction,
	selectNewBusinessMortgageeAction,
	unselectNewBusinessMortgageeAction,
	loadPossibleNewBusinessDisclosureTextsAction,
	loadNewBusinessApplicationDocumentAction,
	loadNewBusinessBindingAuthorityEligibilityResultsAction,
	loadNewBusinessPossiblePerilsAction,
};